import React, { useEffect } from "react";
import OurCompaniesContent from "../contents/OurCompaniesContent";
import WelcomeContent from "../contents/WelcomeContent";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../api/store";
import { getIllness } from "../api/illness/IllnessSlice";
import MobileCarousel from "../components/carousel/MobileCarousel";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
// import WordContent from "../contents/WordContent";
import BannerImageContent from "../contents/BannerImageContent";
import HomeBackground from "../components/carousel/HomeBackground";
import WordContent from "../contents/WordContent";


interface HomePageProps {
  changeLanguage: (code: string) => void;
  currentLang: string;
}


const HomePage: React.FC <HomePageProps>= ({changeLanguage, currentLang}) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const canonicalUrl = `https://www.ceylonhospital.com${location.pathname}`;

  // Schema.org structured data
  const hospitalStructuredData = {
    "@context": "https://schema.org",
    "@type": "Hospital",
    name: "Ceylon Hospital",
    alternateName: "Ceylon Medical Center",
    url: "https://www.ceylonhospital.com",
    logo: "https://www.ceylonhospital.com/images/logo.png",
    image: "https://www.ceylonhospital.com/images/hospital-building.jpg",
    description:
      "Sri Lanka's leading private hospital providing exceptional healthcare services with modern facilities and experienced medical professionals",
    address: {
      "@type": "PostalAddress",
      streetAddress: "No.228/1, Asiri Mawatha, Kandy Road, Yakkala",
      addressLocality: "Gampaha",
      addressRegion: "Western Province",
      postalCode: "11000",
      addressCountry: "Sri Lanka",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "6.9271",
      longitude: "79.8612",
    },
    telephone: "+94712509898",
    openingHours: "24/7",
    priceRange: "$$$",
    medicalSpecialty: [
      "Cardiology",
      "Oncology",
      "Neurology",
      "Pediatrics",
      "Orthopedics",
      "Emergency Medicine",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Medical Services",
      itemListElement: [
        {
          "@type": "OfferCatalog",
          name: "Diagnostic Services",
          itemListElement: [
            { "@type": "Offer", name: "Standard Ayurvedic Treatments" },
            { "@type": "Offer", name: "Convenient Ayurvedic Medications" },
            { "@type": "Offer", name: "Residential Ward Facilities" },
            {
              "@type": "Offer",
              name: "Channeling Services from Over 1,000 Doctors",
            },
            { "@type": "Offer", name: "Preventive Care" },
            { "@type": "Offer", name: "Aftercare" },
          ],
        },
        {
          "@type": "OfferCatalog",
          name: "Treatment Services",
          itemListElement: [
            {
              "@type": "Offer",
              name: "Panchakarma Therapy",
              description: "Complete detoxification and rejuvenation program",
              category: "Detox Treatment",
            },
            {
              "@type": "Offer",
              name: "Abhyanga Massage",
              description:
                "Traditional herbal oil massage for relaxation and healing",
              category: "Body Therapy",
            },
            {
              "@type": "Offer",
              name: "Shirodhara Treatment",
              description:
                "Warm oil flow therapy for stress relief and mental clarity",
              category: "Neurological Care",
            },
            {
              "@type": "Offer",
              name: "Ayurvedic Consultation",
              description:
                "Personalized treatment plans by experienced Ayurvedic physicians",
              category: "Diagnostic Service",
            },
            {
              "@type": "Offer",
              name: "Herbal Medicine",
              description:
                "Customized herbal preparations for various health conditions",
              category: "Pharmaceutical Service",
            },
          ],
        },
      ],
    },
    sameAs: [
      "https://www.facebook.com/CeylonHospital",
      "https://www.youtube.com/@ceylonhospital",
      "https://www.instagram.com/ceylonhospital",
      "https://www.linkedin.com/company/ceylon-hospital",
      "https://www.tiktok.com/@ceylonhospital",
    ],
  };

  useEffect(() => {
    dispatch(getIllness());
  }, [dispatch]);




  
  const handleCountryCodeChange = (code: string) => {
    changeLanguage(code);
  };


  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>
          Ceylon Hospital | Leading Ayurvedic Hospital in Sri Lanka.
        </title>

        <meta
          name="description"
          content="Ceylon Hospital in Gampaha is the leading Ayurvedic hospital in Sri Lanka, renowned for its expert Ayurveda doctors, holistic healing therapies, and proven treatment success. Specializing in authentic Ayurvedic treatments such as Panchakarma, Shirodhara, Abhyanga, and herbal medicine, we blend ancient Ayurvedic wisdom with modern medical advancements to deliver personalized wellness solutions. Our highly qualified and certified physicians create tailored treatment plans for chronic ailments, detoxification, stress relief, and rejuvenation. Trusted by thousands for exceptional Ayurvedic care, Ceylon Hospital provides natural, side-effect-free healing for long-lasting health and well-being. Experience the best of traditional Sri Lankan Ayurveda with cutting-edge therapeutic techniques for a healthier, balanced life."
          // content="Ceylon Hospital is Sri Lanka's premier healthcare provider offering advanced medical treatments, and Ayurvedic therapies. Visit our Gampaha facility for expert doctors, modern facilities, and compassionate care."
        />
        <meta
          name="keywords"
          content="Sri Lanka, Gampaha, Ayurvedic treatments.,holistic healing, Panchakarma, Shirodhara, Ceylon Hospital,Ayurvedic hospital in Sri Lanka, Dr.Daya Madoluwa,Daya Madulowa,devinda hettiarachchi,dr devinda hettiarachchi,Sri Lanka hospital, best hospital Gampaha, medical care Sri Lanka, healthcare services, specialist doctors, emergency care, surgical procedures, ayurveda, colombo hospital, Gampaha hospital, Back Pain, Privet Hospital, Migraine, paththu, Hot bath, sri lanka healthcare"
        />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta
          property="og:title"
          content="Ceylon Hospital | Leading Ayurvedic Hospital in Sri Lanka."
        />
        <meta
          property="og:description"
          content="Sri Lanka's premier hospital offering comprehensive medical services with advanced technology and expert care"
        />
        <meta
          property="og:image"
          content="https://www.ceylonhospital.com/images/og-home.jpg"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Ceylon Hospital" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@CeylonHospital" />
        <meta name="twitter:creator" content="@CeylonHospital" />
        <meta
          name="twitter:title"
          content="Ceylon Hospital | Leading Ayurvedic Hospital in Sri Lanka."
        />
        <meta
          name="twitter:description"
          content="Sri Lanka's premier hospital offering comprehensive medical services with advanced technology and expert care"
        />
        <meta
          name="twitter:image"
          content="https://www.ceylonhospital.com/images/og-home.jpg"
        />

        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Structured data */}
        <script type="application/ld+json">
          {JSON.stringify(hospitalStructuredData)}
        </script>
      </Helmet>

      {/* Main Content */}
      <main>
        {/* Hero Section */}
        <div>
          <div className="block md:hidden">
            <MobileCarousel changeLanguageMob={handleCountryCodeChange} currentLangMob={currentLang} />
          </div>
          <div className="mb-11">
            <HomeBackground currentLang={currentLang} changeLanguage={handleCountryCodeChange} />
            <h1 className="sr-only">
              Leading Ayurvedic Hospital in Sri Lanka.
            </h1>
            <h2 className="sr-only">Ceylon Hospital</h2>
            <p className="sr-only">
              At Ceylon Hospital, we are dedicated to providing compassionate,
              high-quality healthcare with a commitment to excellence. Our team
              of experienced medical professionals, state-of-the-art facilities,
              and patient-centered approach ensure that you receive the best
              possible care. Whether you are here for a routine check-up,
              specialized treatment, or emergency care, we prioritize your
              health and well-being above all else. We strive to create a
              healing environment where you feel safe, supported, and confident
              in your care.Thank you for choosing Ceylon Hospital. Your health
              is our priority.
            </p>
          </div>
        </div>

        {/* Content Sections */}
        <WelcomeContent />
        <div className="mb-5 -mt-10">
          <WordContent />
        </div>
        <BannerImageContent />
        <OurCompaniesContent />
      </main>
    </>
  );
};

export default HomePage;